import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SupportContact = () => {
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    category: 'general',
    priority: 'medium',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Support categories
  const supportCategories = [
    { value: 'general', label: 'Question générale' },
    { value: 'technical', label: 'Problème technique' },
    { value: 'billing', label: 'Facturation' },
    { value: 'account', label: 'Compte utilisateur' },
    { value: 'campaign', label: 'Campagnes marketing' },
    { value: 'api', label: 'API et intégrations' },
    { value: 'feature', label: 'Demande de fonctionnalité' },
    { value: 'bug', label: 'Signalement de bug' }
  ];

  const priorityLevels = [
    { value: 'low', label: 'Faible', color: 'text-success', description: 'Question non urgente' },
    { value: 'medium', label: 'Moyenne', color: 'text-warning', description: 'Problème standard' },
    { value: 'high', label: 'Haute', color: 'text-error', description: 'Problème urgent' },
    { value: 'critical', label: 'Critique', color: 'text-red-600', description: 'Service inaccessible' }
  ];

  const contactMethods = [
    {
      id: 'live-chat',
      title: 'Chat en Direct',
      description: 'Assistance immédiate avec nos experts',
      availability: 'Lun-Ven: 9h-18h',
      responseTime: 'Immédiat',
      icon: 'MessageCircle',
      color: 'bg-green-500',
      action: 'Démarrer le chat'
    },
    {
      id: 'email',
      title: 'Support Email',
      description: 'Envoyez-nous votre question détaillée',
      availability: '24h/24, 7j/7',
      responseTime: '< 4 heures',
      icon: 'Mail',
      color: 'bg-blue-500',
      action: 'Envoyer un email'
    },
    {
      id: 'phone',
      title: 'Support Téléphonique',
      description: 'Appelez directement nos experts',
      availability: 'Lun-Ven: 9h-18h',
      responseTime: 'Immédiat',
      icon: 'Phone',
      color: 'bg-purple-500',
      action: '+33 1 23 45 67 89'
    },
    {
      id: 'community',
      title: 'Forum Communautaire',
      description: 'Partagez avec la communauté Artis.io',
      availability: '24h/24, 7j/7',
      responseTime: 'Variable',
      icon: 'Users',
      color: 'bg-orange-500',
      action: 'Accéder au forum'
    }
  ];

  const handleFormChange = (field, value) => {
    setSupportForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Reset form
    setSupportForm({
      name: '',
      email: '',
      category: 'general',
      priority: 'medium',
      subject: '',
      message: ''
    });
    
    setIsSubmitting(false);
  };

  return (
    <div className="space-y-8">
      {/* Contact Methods */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="MessageSquare" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-card-foreground">Contacter le Support</h2>
            <p className="text-muted-foreground">Choisissez le moyen de contact qui vous convient le mieux</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {contactMethods?.map((method) => (
            <div key={method?.id} className="bg-muted/50 rounded-xl p-6 hover:bg-muted/70 transition-colors">
              <div className={`w-12 h-12 ${method?.color} rounded-lg flex items-center justify-center mb-4`}>
                <Icon name={method?.icon} size={24} className="text-white" />
              </div>
              
              <h3 className="text-lg font-semibold text-card-foreground mb-2">{method?.title}</h3>
              <p className="text-sm text-muted-foreground mb-4">{method?.description}</p>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={14} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{method?.availability}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Zap" size={14} className="text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Réponse: {method?.responseTime}</span>
                </div>
              </div>
              
              <Button variant="outline" className="w-full">
                {method?.action}
              </Button>
            </div>
          ))}
        </div>
      </div>
      {/* Support Form */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-card-foreground mb-2">Formulaire de Support</h3>
          <p className="text-muted-foreground">Décrivez votre problème en détail pour une assistance plus rapide</p>
        </div>

        <form onSubmit={handleFormSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Nom complet *
              </label>
              <Input
                type="text"
                value={supportForm?.name}
                onChange={(e) => handleFormChange('name', e?.target?.value)}
                placeholder="Votre nom complet"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Email *
              </label>
              <Input
                type="email"
                value={supportForm?.email}
                onChange={(e) => handleFormChange('email', e?.target?.value)}
                placeholder="votre.email@exemple.com"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Catégorie *
              </label>
              <select
                value={supportForm?.category}
                onChange={(e) => handleFormChange('category', e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              >
                {supportCategories?.map((category) => (
                  <option key={category?.value} value={category?.value}>
                    {category?.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Priorité *
              </label>
              <div className="grid grid-cols-2 gap-2">
                {priorityLevels?.map((priority) => (
                  <button
                    key={priority?.value}
                    type="button"
                    onClick={() => handleFormChange('priority', priority?.value)}
                    className={`p-2 text-xs rounded-lg border transition-colors ${
                      supportForm?.priority === priority?.value
                        ? 'border-accent bg-accent/10 text-accent' :'border-border bg-background text-muted-foreground hover:border-accent/50'
                    }`}
                  >
                    <div className={`font-medium ${priority?.color}`}>{priority?.label}</div>
                    <div className="text-xs opacity-80">{priority?.description}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Sujet *
            </label>
            <Input
              type="text"
              value={supportForm?.subject}
              onChange={(e) => handleFormChange('subject', e?.target?.value)}
              placeholder="Résumé de votre problème ou question"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-card-foreground mb-2">
              Description détaillée *
            </label>
            <textarea
              value={supportForm?.message}
              onChange={(e) => handleFormChange('message', e?.target?.value)}
              placeholder="Décrivez votre problème en détail. Incluez les étapes pour reproduire le problème si applicable..."
              rows={6}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent resize-none"
              required
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              * Champs obligatoires
            </div>
            <Button
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              <Icon name="Send" size={16} className="mr-2" />
              Envoyer la demande
            </Button>
          </div>
        </form>
      </div>
      {/* Additional Resources */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Ressources Supplémentaires</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Icon name="BookOpen" size={32} className="text-accent" />
            </div>
            <h4 className="font-semibold text-card-foreground mb-2">Base de Connaissances</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Articles détaillés et guides de résolution
            </p>
            <Button variant="outline" size="sm">Consulter</Button>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-success/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Video" size={32} className="text-success" />
            </div>
            <h4 className="font-semibold text-card-foreground mb-2">Tutoriels Vidéo</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Démonstrations visuelles pas à pas
            </p>
            <Button variant="outline" size="sm">Regarder</Button>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-warning/10 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Download" size={32} className="text-warning" />
            </div>
            <h4 className="font-semibold text-card-foreground mb-2">Guides PDF</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Documentation complète à télécharger
            </p>
            <Button variant="outline" size="sm">Télécharger</Button>
          </div>
        </div>
      </div>
      {/* Status Page */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-card-foreground">État du Service</h3>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-success rounded-full"></div>
            <span className="text-sm text-success font-medium">Tous les systèmes opérationnels</span>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name="Server" size={16} className="text-muted-foreground" />
              <span className="text-sm text-card-foreground">API Artis.io</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full"></div>
              <span className="text-xs text-success">Opérationnel</span>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name="Database" size={16} className="text-muted-foreground" />
              <span className="text-sm text-card-foreground">Base de données</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full"></div>
              <span className="text-xs text-success">Opérationnel</span>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Icon name="CreditCard" size={16} className="text-muted-foreground" />
              <span className="text-sm text-card-foreground">Système de paiement</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full"></div>
              <span className="text-xs text-success">Opérationnel</span>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <Button variant="outline" className="w-full">
            <Icon name="ExternalLink" size={16} className="mr-2" />
            Voir la page de statut complète
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SupportContact;